import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, canAccess } from "@/lib/auth";
import CustomerHome from "@/pages/CustomerHome";
import OrderStation from "@/pages/OrderStation";
import Menu from "@/pages/Menu";
import WineDetail from "@/pages/WineDetail";
import Checkout from "@/pages/Checkout";
import OrderStatus from "@/pages/OrderStatus";
import SelectStation from "@/pages/SelectStation";
import StaffDashboard from "@/pages/StaffDashboard";
import StaffShell from "@/pages/staff/StaffShell";
import StaffPinLogin from "@/pages/staff/StaffPinLogin";
import AdminLogin from "@/pages/AdminLogin";
import StaffHome from "@/pages/staff/StaffHome";
import StaffService from "@/pages/staff/StaffService";
import StaffCustomers from "@/pages/staff/StaffCustomers";
import StaffLists from "@/pages/staff/StaffLists";
import StaffAvailability from "@/pages/staff/StaffAvailability";
import StaffSala from "@/pages/staff/StaffSala";
import StaffMessagesInbox from "@/pages/staff/StaffMessagesInbox";
import StaffMessagesThread from "@/pages/staff/StaffMessagesThread";
import StaffScheduledTemplates from "@/pages/staff/StaffScheduledTemplates";
import AdminLayout from "@/pages/AdminLayout";
import AdminMenu from "@/pages/AdminMenu";
import AdminQuickAvailability from "@/pages/AdminQuickAvailability";
import AdminModifierTemplates from "@/pages/AdminModifierTemplates";
import AdminSuggestions from "@/pages/AdminSuggestions";
import AdminMenuHistory from "@/pages/AdminMenuHistory";
import AdminCustomerDetail from "@/pages/AdminCustomerDetail";
import AdminLoyalty from "@/pages/AdminLoyalty";
import PublishDiffPreview from "@/pages/PublishDiffPreview";
import AdminStations from "@/pages/AdminStations";
import AdminUsers from "@/pages/AdminUsers";
import AdminReports from "@/pages/AdminReports";
import AdminCustomers from "@/pages/AdminCustomers";
import AdminCrmLists from "@/pages/AdminCrmLists";
import AdminReservations from "@/pages/AdminReservations";
import StaffAgenda from "@/pages/staff/StaffAgenda";
import ReservationConfirm from "@/pages/ReservationConfirm";
import AdminSettings from "@/pages/AdminSettings";
import AdminRooms from "@/pages/AdminRooms";
import AdminRoomDetail from "@/pages/AdminRoomDetail";
import AdminIntegrations from "@/pages/AdminIntegrations";
import AdminMenus from "@/pages/AdminMenus";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminAvailability from "@/pages/AdminAvailability";
import AdminActivity from "@/pages/AdminActivity";
import AdminSystem from "@/pages/AdminSystem";
import AdminDashboardLive from "@/pages/AdminDashboardLive";
import AdminSetup from "@/pages/AdminSetup";
import ChangePassword from "@/pages/ChangePassword";
import PrintOrder from "@/pages/PrintOrder";
import PrintStation from "@/pages/PrintStation";
import PreviewMenu from "@/pages/PreviewMenu";
import CustomerLayout from "@/components/CustomerLayout";
import BookingShell from "@/pages/reservations/ReservationsShell";
import BookingPinLogin from "@/pages/reservations/ReservationsPinLogin";
import BookingNew from "@/pages/reservations/ReservationsNew";
import CustomerProfileComplete from "@/pages/CustomerProfileComplete";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import "@/App.css";

function StaffGuard({ children, minRole = "runner" }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-ink-500">Caricamento…</div>;
  if (!user) return <Navigate to="/staff/login" replace />;
  if (!canAccess(user, minRole)) return <div className="p-8">Permessi insufficienti</div>;
  return children;
}

// Guard generico Staff: stesso schema di MessagingGuard, permesso parametrico.
function StaffPermGuard({ perm, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-ink-500">Caricamento…</div>;
  if (!user) return <Navigate to="/staff/login" replace />;
  const eff = user.effective_permissions || [];
  if (user.role !== "super_admin" && !eff.includes(perm)) {
    return <div data-testid="staff-forbidden" className="p-8 text-ink-500">
      Modulo non autorizzato per il tuo profilo.
    </div>;
  }
  return children;
}

// M2.2 · Guard specifico per la sezione Messaggi Staff.
// Super Admin bypass via effective_permissions (già gestito dal backend).
function MessagingGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-ink-500">Caricamento…</div>;
  if (!user) return <Navigate to="/staff/login" replace />;
  const isSuper = user.role === "super_admin";
  const eff = user.effective_permissions || [];
  if (!isSuper && !eff.includes("staff.messaging")) {
    return <div data-testid="messaging-forbidden" className="p-8 text-ink-500">
      Permessi insufficienti per accedere ai Messaggi.
    </div>;
  }
  return children;
}

/**
 * AdminGuard — Guard DEDICATO al backoffice Admin.
 *
 * Se non c'è sessione → reindirizza a /admin/login (email/password),
 * NON alla tastiera PIN Staff. Le due auth restano completamente
 * separate: Staff → PIN, Admin → email/pw. Un utente PIN-authenticated
 * con ruolo manager+ viene comunque accettato qui (unified session).
 *
 * `perm` = permesso RBAC richiesto (fine-grained). Super Admin bypassa.
 * `minRole` = livello ruolo minimo (safety net gerarchico).
 * Se l'utente fallisce il check ma è un `meta_reviewer` con `admin.integrations`,
 * viene reindirizzato alla sua unica pagina consentita `/admin/integrations`.
 * Altrimenti a `/staff/login` (safe fallback).
 */
function AdminGuard({ children, minRole = "manager", perm = "admin.access" }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8 text-ink-500">Caricamento…</div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  // Super Admin bypass + explicit permission check.
  const isSuper = user.role === "super_admin";
  const eff = user.effective_permissions || [];
  const hasPerm = isSuper || eff.includes(perm);
  const roleOk = canAccess(user, minRole);
  if (!hasPerm || !roleOk) {
    // Safe landing: meta_reviewer (o chiunque abbia solo admin.integrations)
    // torna sempre alla sua pagina di destinazione consentita per evitare
    // loop e dead-end su /staff.
    if (!isSuper && eff.includes("admin.integrations")
        && loc.pathname !== "/admin/integrations") {
      return <Navigate to="/admin/integrations" replace />;
    }
    return <Navigate to="/staff/login" replace />;
  }
  return children;
}

function App() {
  useEffect(() => { document.title = "Sarabi Beach — Ordina"; }, []);
  return (
    <AuthProvider>
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route element={<CustomerLayout />}>
            <Route path="/" element={<CustomerHome />} />
            <Route path="/order/:token" element={<OrderStation />} />
            <Route path="/menu" element={<Menu />} />
            <Route path="/wine/:productId" element={<WineDetail />} />
            <Route path="/select-station" element={<SelectStation />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/status/:orderId" element={<OrderStatus />} />
          </Route>

          <Route path="/staff/login" element={<StaffPinLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/staff" element={<StaffShell />}>
            <Route index element={<StaffHome />} />
            <Route path="ordini" element={<StaffDashboard />} />
            <Route path="agenda" element={<StaffPermGuard perm="staff.agenda"><StaffAgenda /></StaffPermGuard>} />
            <Route path="servizio" element={<StaffPermGuard perm="staff.service"><StaffService /></StaffPermGuard>} />
            <Route path="clienti" element={<StaffPermGuard perm="staff.clients"><StaffCustomers /></StaffPermGuard>} />
            <Route path="lists" element={<StaffPermGuard perm="staff.clients"><StaffLists /></StaffPermGuard>} />
            <Route path="lists/:listId" element={<StaffPermGuard perm="staff.clients"><StaffLists /></StaffPermGuard>} />
            <Route path="sala" element={<StaffPermGuard perm="staff.live_room"><StaffSala /></StaffPermGuard>} />
            <Route path="disponibilita" element={<StaffPermGuard perm="staff.availability"><StaffAvailability /></StaffPermGuard>} />
            <Route path="messages" element={<MessagingGuard><StaffMessagesInbox /></MessagingGuard>} />
            <Route path="messages/:conversationId" element={<MessagingGuard><StaffMessagesThread /></MessagingGuard>} />
            <Route path="scheduled-templates" element={<StaffScheduledTemplates />} />
          </Route>
          <Route path="/account/password" element={<StaffGuard><ChangePassword /></StaffGuard>} />

          <Route path="/admin" element={<AdminGuard minRole="meta_reviewer" perm="admin.access"><AdminLayout /></AdminGuard>}>
            <Route index element={<AdminGuard minRole="manager" perm="admin.dashboard"><AdminDashboardLive /></AdminGuard>} />
            <Route path="reports" element={<AdminGuard minRole="manager" perm="admin.reports"><AdminReports /></AdminGuard>} />
            <Route path="activity" element={<AdminGuard minRole="manager" perm="admin.activity"><AdminActivity /></AdminGuard>} />
            <Route path="system" element={<AdminGuard minRole="admin" perm="admin.system"><AdminSystem /></AdminGuard>} />
            <Route path="menu" element={<AdminGuard minRole="manager" perm="admin.menu"><AdminMenu /></AdminGuard>} />
            <Route path="menu/diff" element={<AdminGuard minRole="manager" perm="admin.menu"><PublishDiffPreview /></AdminGuard>} />
            <Route path="menu/templates" element={<AdminGuard minRole="manager" perm="admin.templates"><AdminModifierTemplates /></AdminGuard>} />
            <Route path="menu/suggestions" element={<AdminGuard minRole="manager" perm="admin.suggestions"><AdminSuggestions /></AdminGuard>} />
            <Route path="menu/history" element={<AdminGuard minRole="manager" perm="admin.menu_history"><AdminMenuHistory /></AdminGuard>} />
            <Route path="availability" element={<AdminGuard minRole="manager" perm="admin.availability"><AdminQuickAvailability /></AdminGuard>} />
            <Route path="availability" element={<AdminGuard minRole="manager" perm="admin.availability"><AdminAvailability /></AdminGuard>} />
            <Route path="menus" element={<AdminGuard minRole="manager" perm="admin.menus"><AdminMenus /></AdminGuard>} />
            <Route path="audit" element={<AdminGuard minRole="admin" perm="admin.audit"><AdminAuditLog /></AdminGuard>} />
            <Route path="stations" element={<AdminGuard minRole="manager" perm="admin.qr"><AdminStations /></AdminGuard>} />
            <Route path="users" element={<AdminGuard minRole="admin" perm="admin.users"><AdminUsers /></AdminGuard>} />
            <Route path="customers" element={<AdminGuard minRole="manager" perm="admin.crm"><AdminCustomers /></AdminGuard>} />
            <Route path="customers/:cid" element={<AdminGuard minRole="manager" perm="admin.crm"><AdminCustomerDetail /></AdminGuard>} />
            <Route path="crm/lists" element={<AdminGuard minRole="manager" perm="admin.crm"><AdminCrmLists /></AdminGuard>} />
            <Route path="reservations" element={<AdminGuard minRole="manager" perm="admin.reservations"><AdminReservations /></AdminGuard>} />
            <Route path="loyalty" element={<AdminGuard minRole="manager" perm="admin.loyalty"><AdminLoyalty /></AdminGuard>} />
            <Route path="settings" element={<AdminGuard minRole="admin" perm="admin.settings"><AdminSettings /></AdminGuard>} />
            {/* Retrocompatibilità Fase 1: /admin/settings/rooms → /admin/rooms */}
            <Route path="settings/rooms" element={<Navigate to="/admin/rooms" replace />} />
            <Route path="rooms" element={<AdminGuard minRole="admin" perm="admin.settings"><AdminRooms /></AdminGuard>} />
            <Route path="rooms/:room_id" element={<AdminGuard minRole="admin" perm="admin.settings"><AdminRoomDetail /></AdminGuard>} />
            <Route path="integrations" element={<AdminGuard minRole="meta_reviewer" perm="admin.integrations"><AdminIntegrations /></AdminGuard>} />
          </Route>

          <Route path="/preview-menu" element={<PreviewMenu />} />
          <Route path="/reservations/:token/confirm" element={<ReservationConfirm />} />

          {/* Sarabi Reservations — quarta app dell'ecosistema. PIN login separato.
              Alias /booking/* mantenuto per continuità dei bookmark dei tester. */}
          <Route path="/reservations/login" element={<BookingPinLogin />} />
          <Route path="/reservations" element={<BookingShell />}>
            <Route index element={<BookingNew />} />
          </Route>
          <Route path="/booking/login" element={<Navigate to="/reservations/login" replace />} />
          <Route path="/booking" element={<Navigate to="/reservations" replace />} />
          {/* Pagina pubblica cliente "completa profilo" (link WhatsApp Sarabi Reservations) */}
          <Route path="/p/:token" element={<CustomerProfileComplete />} />

          <Route path="/print/:orderId" element={<StaffGuard><PrintOrder /></StaffGuard>} />
          <Route path="/print-station" element={<StaffGuard><PrintStation /></StaffGuard>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
export default App;
